import { FiltreConfig } from '../FikoSPHParametreler';
import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';
import { Vector3 } from 'three';

/**
 * FikoFiltre - Filter element for SPH fluid simulation
 * GPU struct size: 64 bytes
 *
 * Layout:
 * - pozX, pozY, pozZ, boyut (16 bytes)
 * - yonX, yonY, yonZ, gozenekBoyutu (16 bytes)
 * - gecirgenlik, basincDususu, kapasite, gecenMiktar (16 bytes)
 * - aktif as u32, pad, pad, pad (16 bytes)
 */
export class FikoFiltre implements ISPHEleman {
  readonly id: string;
  readonly tip: ElemanTipi = ElemanTipi.FILTRE;

  pozisyon: Vector3;
  yon: Vector3;
  boyut: { genislik: number; yukseklik: number };
  gozenekBoyutu: number;
  gecirgenlik: number;
  basincDususu: number;
  kapasite: number;
  aktif: boolean;
  ad: string;

  // Runtime state (not in config, but tracked for GPU sync)
  private gecenMiktar: number = 0;

  constructor(config: Partial<FiltreConfig> = {}) {
    this.id = config.id || olusturId();

    this.pozisyon = config.pozisyon ? new Vector3(config.pozisyon.x, config.pozisyon.y, config.pozisyon.z) : new Vector3(0, 0, 0);
    this.yon = config.yon ? new Vector3(config.yon.x, config.yon.y, config.yon.z).normalize() : new Vector3(0, 1, 0);
    this.boyut = config.boyut ?? { genislik: 0.05, yukseklik: 0.05 };
    this.gozenekBoyutu = config.gozenekBoyutu ?? 0.001;
    this.gecirgenlik = config.gecirgenlik ?? 0.5;
    this.basincDususu = config.basincDususu ?? 100;
    this.kapasite = config.kapasite ?? Infinity;
    this.aktif = config.aktif ?? true;
    this.ad = config.ad || 'Filtre';
  }

  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);
    let offset = 0;

    // Chunk 1: pozX, pozY, pozZ, boyutGenislik (16 bytes)
    view.setFloat32(offset, this.pozisyon.x, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.y, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.z, true); offset += 4;
    view.setFloat32(offset, this.boyut.genislik, true); offset += 4;

    // Chunk 2: yonX, yonY, yonZ, gozenekBoyutu (16 bytes)
    view.setFloat32(offset, this.yon.x, true); offset += 4;
    view.setFloat32(offset, this.yon.y, true); offset += 4;
    view.setFloat32(offset, this.yon.z, true); offset += 4;
    view.setFloat32(offset, this.gozenekBoyutu, true); offset += 4;

    // Chunk 3: gecirgenlik, basincDususu, kapasite, gecenMiktar (16 bytes)
    view.setFloat32(offset, this.gecirgenlik, true); offset += 4;
    view.setFloat32(offset, this.basincDususu, true); offset += 4;
    view.setFloat32(offset, isFinite(this.kapasite) ? this.kapasite : 1e38, true); offset += 4;
    view.setFloat32(offset, this.gecenMiktar, true); offset += 4;

    // Chunk 4: aktif as u32, pad, pad, pad (16 bytes)
    view.setUint32(offset, this.aktif ? 1 : 0, true); offset += 4;
    view.setUint32(offset, 0, true); offset += 4; // pad
    view.setUint32(offset, 0, true); offset += 4; // pad
    view.setUint32(offset, 0, true); // pad

    return buffer;
  }

  gpuStructBoyut(): number {
    return 64;
  }

  toJSON(): any {
    return {
      id: this.id,
      tip: ElemanTipi[this.tip],
      pozisyon: { x: this.pozisyon.x, y: this.pozisyon.y, z: this.pozisyon.z },
      yon: { x: this.yon.x, y: this.yon.y, z: this.yon.z },
      boyut: this.boyut,
      gozenekBoyutu: this.gozenekBoyutu,
      gecirgenlik: this.gecirgenlik,
      basincDususu: this.basincDususu,
      kapasite: this.kapasite,
      gecenMiktar: this.gecenMiktar,
      aktif: this.aktif,
      ad: this.ad
    };
  }

  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.boyut.genislik <= 0 || this.boyut.yukseklik <= 0) {
      hatalar.push('Boyut değerleri pozitif olmalıdır');
    }

    if (this.gozenekBoyutu <= 0) {
      hatalar.push('Gözenek boyutu pozitif olmalıdır');
    }

    const minBoyut = Math.min(this.boyut.genislik, this.boyut.yukseklik);
    if (this.gozenekBoyutu >= minBoyut) {
      hatalar.push('Gözenek boyutu, filtre boyutundan küçük olmalıdır');
    }

    if (this.gecirgenlik < 0 || this.gecirgenlik > 1) {
      hatalar.push('Geçirgenlik 0-1 arasında olmalıdır');
    }

    if (this.basincDususu < 0) {
      hatalar.push('Basınç düşüşü negatif olamaz');
    }

    if (this.kapasite < 0 && isFinite(this.kapasite)) {
      hatalar.push('Kapasite negatif olamaz');
    }

    if (this.yon.lengthSq() < 0.99 || this.yon.lengthSq() > 1.01) {
      hatalar.push('Yön vektörü normalize edilmeli');
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  klonla(): FikoFiltre {
    const kopya = new FikoFiltre({
      pozisyon: this.pozisyon.clone(),
      yon: this.yon.clone(),
      boyut: { genislik: this.boyut.genislik, yukseklik: this.boyut.yukseklik },
      gozenekBoyutu: this.gozenekBoyutu,
      gecirgenlik: this.gecirgenlik,
      basincDususu: this.basincDususu,
      kapasite: this.kapasite,
      aktif: this.aktif,
      ad: this.ad + ' (Kopya)'
    });
    kopya.gecenMiktar = 0; // Reset runtime state
    return kopya;
  }

  parametreListesi(): string[] {
    return [
      'pozisyon', 'yon', 'boyut', 'gozenekBoyutu',
      'gecirgenlik', 'basincDususu', 'kapasite', 'aktif', 'ad'
    ];
  }

  parametreAyarla(anahtar: string, deger: any): boolean {
    switch (anahtar) {
      case 'pozisyon':
        if (deger instanceof Vector3) {
          this.pozisyon.copy(deger);
          return true;
        }
        break;
      case 'yon':
        if (deger instanceof Vector3) {
          this.yon.copy(deger).normalize();
          return true;
        }
        break;
      case 'boyut':
      case 'gozenekBoyutu':
      case 'gecirgenlik':
      case 'basincDususu':
      case 'kapasite':
        if (typeof deger === 'number') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'aktif':
        if (typeof deger === 'boolean') {
          this.aktif = deger;
          return true;
        }
        break;
      case 'ad':
        if (typeof deger === 'string') {
          this.ad = deger;
          return true;
        }
        break;
    }
    return false;
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(anahtar: string): any {
    return (this as any)[anahtar];
  }

  /**
   * Update the amount of material that has passed through the filter
   * (Called by simulation engine)
   */
  guncelleMiktar(miktar: number): void {
    this.gecenMiktar = Math.min(this.gecenMiktar + miktar, isFinite(this.kapasite) ? this.kapasite : 1e38);
  }

  /**
   * Check if filter is saturated
   */
  doluMu(): boolean {
    return isFinite(this.kapasite) && this.gecenMiktar >= this.kapasite;
  }

  /**
   * Reset filter capacity
   */
  sifirla(): void {
    this.gecenMiktar = 0;
  }

  /**
   * Get current fill percentage (0-1)
   */
  dolulukOrani(): number {
    if (!isFinite(this.kapasite)) return 0;
    return Math.min(this.gecenMiktar / this.kapasite, 1);
  }
}

/**
 * Factory function to create default filter element
 */
export function varsayilanFiltre(config?: Partial<FiltreConfig>): FikoFiltre {
  return new FikoFiltre(config);
}
